import {
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  OpenInNew as OpenInNewIcon,
  Psychology as PsychologyIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Link,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import DomainFactCheckHistorySidebar from '../components/DomainFactCheckHistorySidebar';
import DomainListManager from '../components/DomainListManager';
import WorkspaceShareDialog from '../components/WorkspaceShareDialog';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import {
  deleteDomainFactCheckHistory,
  getDomainFactCheckHistory,
  saveDomainFactCheckHistory,
} from '../services/domainFactCheckHistoryService';
import {
  generateQueries,
  getJudgementColor,
  getJudgementLabel,
  verifyWithDomains,
} from '../services/domainFactCheckService';
import { getDomainLists } from '../services/domainListService';
import { createFactCheckItem, updateFactCheckItem } from '../services/factCheckItemService';
import { IMG } from '../theme/assets';
import { verdictSealFromLabel } from '../theme/designTokens';
import type {
  DomainFactCheckHistoryItem,
  DomainList,
  DomainVerificationResult,
  TavilyTimeRange,
} from '../types';

// 画面フェーズの型
type Phase = 'initial' | 'query_generated' | 'result_shown';

const DomainFactCheck: React.FC = () => {
  const { user } = useAuth();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();
  const dark = themeMode === 'dark';

  // テーマカラー変数（mock 準拠 / トークン参照）
  const cardBg = tokens.paper;
  const accordionBg = dark ? 'rgba(255, 255, 255, 0.04)' : '#fff';
  // mock の主要グラデーション（CTA・判定見出し）
  const ctaGradient = 'linear-gradient(120deg,#0d9488,#5b56e0)';

  // 画面フェーズ管理
  const [phase, setPhase] = useState<Phase>('initial');

  // 入力状態
  const [userInput, setUserInput] = useState('');
  const [claim, setClaim] = useState('');
  const [suggestedQueries, setSuggestedQueries] = useState<string[]>([]);
  const [selectedQuery, setSelectedQuery] = useState('');
  const [customQuery, setCustomQuery] = useState('');

  // ドメインリスト
  const [domainLists, setDomainLists] = useState<DomainList[]>([]);
  const [selectedListIds, setSelectedListIds] = useState<string[]>([]);
  const [loadingLists, setLoadingLists] = useState(true);

  // 処理状態
  const [generatingQueries, setGeneratingQueries] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState<DomainVerificationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 検索期間オプション
  const [timeRange, setTimeRange] = useState<TavilyTimeRange | ''>('');

  // 履歴管理
  const [history, setHistory] = useState<DomainFactCheckHistoryItem[]>([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // UI状態
  const [showDomainManager, setShowDomainManager] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareTarget, setShareTarget] = useState<DomainFactCheckHistoryItem | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadDomainLists = useCallback(async () => {
    if (!user?.uid) return;

    setLoadingLists(true);
    try {
      const response = await getDomainLists();
      if (response.success && response.data) {
        setDomainLists(response.data);
        // デフォルトでは何も選択しない（ドメイン数が少ないほど精度向上のため）
        setSelectedListIds([]);
      }
    } catch (err) {
      console.error('Failed to load domain lists:', err);
    } finally {
      setLoadingLists(false);
    }
  }, [user?.uid]);

  const loadHistory = useCallback(async () => {
    if (!user?.uid) return;

    setLoadingHistory(true);
    try {
      const historyData = await getDomainFactCheckHistory(user.uid);
      setHistory(historyData);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [user?.uid]);

  // ドメインリストを読み込み
  useEffect(() => {
    if (user?.uid) {
      loadDomainLists();
      loadHistory();
    }
  }, [user?.uid, loadHistory, loadDomainLists]);

  // クエリ生成
  const handleGenerateQueries = async () => {
    if (!user?.uid || !userInput.trim()) return;

    setGeneratingQueries(true);
    setError(null);
    setResult(null);
    setSelectedHistoryId(null);

    try {
      const response = await generateQueries(userInput.trim());
      if (response.success && response.data) {
        setClaim(response.data.claim);
        setSuggestedQueries(response.data.suggested_queries);
        setSelectedQuery(response.data.suggested_queries[0] || '');
        setPhase('query_generated');
      } else {
        setError(response.error || 'クエリ生成に失敗しました');
      }
    } catch (_err) {
      setError('クエリ生成中にエラーが発生しました');
    } finally {
      setGeneratingQueries(false);
    }
  };

  // Tavilyのドメイン上限
  const MAX_DOMAINS = 300;

  // ファクト検証実行
  const handleVerify = async () => {
    if (!user?.uid || !claim.trim() || selectedListIds.length === 0) return;

    // ドメイン数上限チェック
    if (selectedDomainsCount > MAX_DOMAINS) {
      setError(`ドメイン数が上限（${MAX_DOMAINS}件）を超えています。選択を減らしてください。`);
      return;
    }

    const queryToUse = customQuery.trim() || selectedQuery;
    if (!queryToUse) {
      setError('検索クエリを選択または入力してください');
      return;
    }

    setVerifying(true);
    setError(null);

    try {
      const response = await verifyWithDomains({
        claim: claim.trim(),
        query: queryToUse,
        domain_list_ids: selectedListIds,
        time_range: null, // 検索期間は指定しない（精度向上のため）
      });

      if (response.success && response.data) {
        setResult(response.data);
        setPhase('result_shown');

        // 履歴に保存
        try {
          const historyId = await saveDomainFactCheckHistory(user.uid, {
            claim: claim.trim(),
            query: queryToUse,
            user_input: userInput.trim(),
            domain_list_ids: selectedListIds,
            time_range: null, // 検索期間は指定しない（精度向上のため）
            result: response.data,
          });
          setSelectedHistoryId(historyId);
          await loadHistory(); // 履歴を再読み込み
        } catch (historyError) {
          console.error('Failed to save history:', historyError);
        }
      } else {
        setError(response.error || '検証に失敗しました');
      }
    } catch (_err) {
      setError('検証中にエラーが発生しました');
    } finally {
      setVerifying(false);
    }
  };

  // リセット（新規検証）
  const handleReset = () => {
    setUserInput('');
    setClaim('');
    setSuggestedQueries([]);
    setSelectedQuery('');
    setCustomQuery('');
    setResult(null);
    setError(null);
    setPhase('initial');
    setSelectedHistoryId(null);
    setSelectedListIds([]); // ドメインリストもリセット
    setTimeRange(''); // 検索期間もリセット
  };

  // 履歴選択
  const handleSelectHistory = (item: DomainFactCheckHistoryItem) => {
    setSelectedHistoryId(item.id);
    setUserInput(item.user_input);
    setClaim(item.claim);
    setSelectedQuery(item.query);
    setCustomQuery('');
    setSuggestedQueries([]);
    setSelectedListIds(item.domain_list_ids);
    setTimeRange(item.time_range || '');
    setResult(item.result);
    setPhase('result_shown');
  };

  // 履歴削除
  const handleDeleteHistory = async (historyId: string) => {
    if (!user?.uid) return;

    try {
      await deleteDomainFactCheckHistory(user.uid, historyId);
      await loadHistory();
      if (selectedHistoryId === historyId) {
        handleReset();
      }
      setSnackbar({ open: true, message: '履歴を削除しました', severity: 'success' });
    } catch (_err) {
      setSnackbar({ open: true, message: '履歴の削除に失敗しました', severity: 'error' });
    }
  };

  // 共有（結果画面から）
  const handleShareCurrentResult = () => {
    if (!result) return;
    setShareTarget({
      id: 'current',
      user_id: user?.uid || '',
      claim,
      query: customQuery || selectedQuery,
      user_input: userInput,
      domain_list_ids: selectedListIds,
      time_range: timeRange || null,
      result,
      created_at: new Date().toISOString(),
    });
    setShowShareDialog(true);
  };

  // 共有（履歴から）
  const handleShareFromHistory = (item: DomainFactCheckHistoryItem) => {
    setShareTarget(item);
    setShowShareDialog(true);
  };

  // 案件に共有実行
  const handleShareToWorkspace = async (workspaceId: string) => {
    if (!user?.uid || !shareTarget) return;

    // Step 1: 基本アイテムを作成（type: 'fact' で作成）
    const createdItem = await createFactCheckItem({
      workspaceId,
      type: 'fact',
      title: shareTarget.claim,
      description: shareTarget.result.summary,
    });

    // Step 2: type: 'fact' 用のフィールドと検証結果を追加
    await updateFactCheckItem(createdItem.id, {
      status: 'completed',
      // type: 'fact' 専用フィールド
      claim: shareTarget.claim,
      verification_result: shareTarget.result.judgement,
      source: {
        summary: shareTarget.result.summary,
        reason: shareTarget.result.reason,
        sources: shareTarget.result.sources,
        thought_process: shareTarget.result.thought_process,
      },
      // 互換性のため従来のフィールドも保持
      facts: [
        {
          claim: shareTarget.claim,
          quote: shareTarget.result.summary,
          verification_strategy: 'google_search',
          page: 1,
          line_start: 1,
          confidence: 1.0,
        },
      ],
      verification_results: [
        {
          claim: shareTarget.claim,
          result: shareTarget.result.judgement,
          source: JSON.stringify({
            judgement: shareTarget.result.judgement,
            summary: shareTarget.result.summary,
            reason: shareTarget.result.reason,
            sources: shareTarget.result.sources,
            thought_process: shareTarget.result.thought_process,
          }),
          quote: shareTarget.result.summary,
          confidence: 1.0,
        },
      ],
      total_facts: 1,
      verified_facts: 1,
    });

    setSnackbar({ open: true, message: '案件に共有しました', severity: 'success' });
    setShowShareDialog(false);
    setShareTarget(null);
  };

  // ドメインリスト選択トグル
  const handleToggleDomainList = (listId: string) => {
    setSelectedListIds((prev) =>
      prev.includes(listId) ? prev.filter((id) => id !== listId) : [...prev, listId],
    );
  };

  // 選択中のドメイン総数を計算
  const selectedDomainsCount = domainLists
    .filter((list) => selectedListIds.includes(list.id))
    .reduce((sum, list) => sum + list.domains.length, 0);

  // mock の検証対象ドメイン pill 描画（横並び維持）
  const sortedDomainLists = [...domainLists].sort((a, b) => {
    // 報道機関（国内主要）を最初に表示
    const priority: Record<string, number> = {
      'system-news-japan': 0,
      'system-news-global': 1,
      'system-gov-japan': 2,
      'system-academic-japan': 3,
      'system-medical-health': 4,
      'system-statistics-data': 5,
    };
    return (priority[a.id] ?? 99) - (priority[b.id] ?? 99);
  });

  const showResult = phase === 'result_shown' && result;

  // パネル共通スタイル
  const panelSx = {
    background: cardBg,
    border: `1px solid ${tokens.line}`,
    borderRadius: '12px',
    p: { xs: 2, sm: '17px 19px' },
    boxShadow: '0 6px 18px rgba(30,30,70,.05)',
  } as const;

  const stepBadge = (n: number) => (
    <Box
      component="span"
      sx={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        bgcolor: tokens.teal,
        color: '#fff',
        fontSize: 12,
        fontWeight: 900,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
      }}
    >
      {n}
    </Box>
  );

  // 入力欄の共通スタイル（mock .fld input/textarea）
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '11px',
      backgroundColor: dark ? 'rgba(255,255,255,0.04)' : '#fff',
      '& fieldset': { borderColor: tokens.line, borderWidth: '1.5px' },
      '&:hover fieldset': { borderColor: 'rgba(13,148,136,.45)' },
      '&.Mui-focused fieldset': { borderColor: tokens.teal },
    },
  } as const;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(var(--app-vh) - 128px)',
        maxWidth: 1440,
        mx: 'auto',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* ヘッダー。左メニューから移動できるので戻るボタンは置かない */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 21, fontWeight: 900, color: tokens.ink }}>
          ドメイン限定ファクトチェック
        </Typography>
        <Button
          startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
          onClick={() => setShowDomainManager(true)}
          disableElevation
          disableRipple
          sx={{
            ml: 'auto',
            textTransform: 'none',
            fontSize: 12.5,
            fontWeight: 800,
            color: tokens.ink,
            bgcolor: dark ? 'rgba(255,255,255,0.05)' : '#fff',
            border: `1.5px solid ${tokens.line}`,
            borderRadius: '11px',
            px: 1.75,
            py: 1,
            boxShadow: 'none',
            '&:hover': { borderColor: tokens.teal, color: tokens.teal, bgcolor: 'transparent' },
            // モーダルを閉じた後にフォーカスが戻っても通常の見た目を保つ
            '&:focus, &:focus-visible': { outline: 'none', boxShadow: 'none' },
            '&.Mui-focusVisible': {
              bgcolor: dark ? 'rgba(255,255,255,0.05)' : '#fff',
              boxShadow: 'none',
            },
          }}
        >
          ドメイン管理
        </Button>
      </Box>

      {/* 2カラム：履歴 + メインエリア */}
      <Box
        sx={{
          display: 'flex',
          gap: 2.5,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* 左サイドバー：履歴 */}
        <Box
          sx={{
            flex: '0 0 240px',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            minHeight: 0,
            background: cardBg,
            border: `1px solid ${tokens.line}`,
            borderRadius: '12px',
            boxShadow: '0 6px 18px rgba(30,30,70,.05)',
            overflow: 'hidden',
          }}
        >
          <DomainFactCheckHistorySidebar
            history={history}
            selectedId={selectedHistoryId}
            onSelect={handleSelectHistory}
            onDelete={handleDeleteHistory}
            onShare={handleShareFromHistory}
            onNewVerification={handleReset}
            loading={loadingHistory}
          />
        </Box>

        {/* メインエリア */}
        <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', pr: 0.5 }}>
          {/* ヒーロー */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.75,
              background: dark
                ? `linear-gradient(120deg, ${tokens.heroFrom}, ${tokens.heroTo})`
                : 'linear-gradient(120deg,#fff6ea,#eafaf6)',
              border: `1px solid ${tokens.line}`,
              borderRadius: '12px',
              p: '11px 18px',
              mb: 1.75,
              boxShadow: '0 8px 24px rgba(30,30,70,.06)',
            }}
          >
            <Box
              component="img"
              src={IMG.owl.clipboard}
              alt=""
              sx={{
                width: 50,
                height: 50,
                objectFit: 'contain',
                flexShrink: 0,
                filter: 'drop-shadow(0 6px 12px rgba(30,30,70,.16))',
              }}
            />
            <Box>
              <Typography sx={{ fontSize: 15.5, fontWeight: 900, color: tokens.ink, m: 0 }}>
                信頼できるドメインだけで照合。
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: tokens.muted, mt: 0.4, lineHeight: 1.45 }}>
                主張を入力すると検索クエリを生成し、選んだ信頼ドメインの情報だけを根拠に検証します。
              </Typography>
            </Box>
          </Box>

          {/* フロー（結果表示時は2カラム） */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: showResult ? '1fr 1fr' : '1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            {/* 入力パネル */}
            <Box sx={panelSx}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 900,
                  color: tokens.ink,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.6,
                }}
              >
                {stepBadge(1)}調べたい内容
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={phase === 'initial' ? 3 : 2}
                placeholder="例: 2024年の日本の出生数は過去最低を記録した"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                disabled={generatingQueries || phase === 'result_shown'}
                sx={{ mb: 1.6, ...fieldSx }}
              />
              {phase !== 'result_shown' && (
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={
                    generatingQueries ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SearchIcon sx={{ fontSize: 18 }} />
                    )
                  }
                  onClick={handleGenerateQueries}
                  disabled={!userInput.trim() || generatingQueries}
                  sx={{
                    background: ctaGradient,
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: 14,
                    borderRadius: '12px',
                    px: 2.25,
                    py: 1.25,
                    textTransform: 'none',
                    boxShadow: '0 8px 20px rgba(40,50,90,.2)',
                    '&:hover': { background: ctaGradient, filter: 'brightness(0.96)' },
                    '&.Mui-disabled': {
                      background: dark ? 'rgba(255,255,255,0.12)' : '#cbd2e0',
                      color: '#fff',
                    },
                  }}
                >
                  {generatingQueries ? '分析中...' : '主張と検索クエリを生成'}
                </Button>
              )}

              {/* Phase 2/3：主張・クエリ・ドメイン */}
              {(phase === 'query_generated' || phase === 'result_shown') && (
                <Box
                  sx={{
                    mt: 1.75,
                    animation: 'fadeSlideIn 0.6s ease-out',
                    '@keyframes fadeSlideIn': {
                      '0%': { opacity: 0, transform: 'translateY(20px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' },
                    },
                  }}
                >
                  {/* 主張 */}
                  <Box sx={{ mb: 1.6 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mb: 0.9,
                        fontSize: 12,
                        fontWeight: 800,
                        color: tokens.muted,
                      }}
                    >
                      検証する主張
                      <Tooltip title="この主張が正確かどうかを検証します">
                        <HelpIcon sx={{ fontSize: 14, color: tokens.faint }} />
                      </Tooltip>
                    </Box>
                    <TextField
                      fullWidth
                      value={claim}
                      onChange={(e) => setClaim(e.target.value)}
                      size="small"
                      disabled={phase === 'result_shown'}
                      sx={fieldSx}
                    />
                  </Box>

                  {/* 検索クエリ */}
                  <Box sx={{ mb: 1.6 }}>
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 800, color: tokens.muted, mb: 0.9 }}
                    >
                      検索クエリ（クリックで選択）
                    </Typography>
                    {suggestedQueries.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.1 }}>
                        {suggestedQueries.map((query, index) => {
                          const on = selectedQuery === query && !customQuery;
                          return (
                            <Box
                              component="button"
                              type="button"
                              key={index}
                              onClick={() => {
                                if (phase !== 'result_shown') {
                                  setSelectedQuery(query);
                                  setCustomQuery('');
                                }
                              }}
                              sx={{
                                fontFamily: 'inherit',
                                fontSize: 12,
                                fontWeight: 700,
                                color: on ? tokens.teal : tokens.muted,
                                bgcolor: on
                                  ? dark
                                    ? 'rgba(45,212,191,0.15)'
                                    : '#eafaf6'
                                  : dark
                                    ? 'rgba(255,255,255,0.05)'
                                    : '#f2f4f8',
                                border: `1.5px solid ${on ? tokens.teal : 'transparent'}`,
                                borderRadius: 999,
                                px: 1.6,
                                py: 0.85,
                                cursor: phase === 'result_shown' ? 'default' : 'pointer',
                                transition: '.12s',
                                '&:hover':
                                  phase === 'result_shown'
                                    ? {}
                                    : { borderColor: 'rgba(13,148,136,.4)' },
                              }}
                            >
                              {query}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="カスタムクエリを入力..."
                      value={customQuery || selectedQuery}
                      onChange={(e) => setCustomQuery(e.target.value)}
                      disabled={phase === 'result_shown'}
                      sx={fieldSx}
                    />
                  </Box>

                  {/* ドメインリスト選択（横並び維持） */}
                  <Box sx={{ mb: 1.6 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 0.5,
                        fontSize: 12,
                        fontWeight: 800,
                        color: tokens.muted,
                      }}
                    >
                      検索対象ドメイン
                      <Box
                        component="span"
                        onClick={() => setShowDomainManager(true)}
                        sx={{
                          ml: 'auto',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: 11,
                          fontWeight: 800,
                          color: tokens.teal,
                          cursor: 'pointer',
                        }}
                      >
                        <SettingsIcon sx={{ fontSize: 13 }} />
                        ドメイン管理
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: 11, color: tokens.faint, display: 'block', mb: 1 }}>
                      対象ドメイン数が少ないほど検索精度が向上しますので、必要最低限のリストを指定してください。
                    </Typography>

                    {loadingLists ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.1 }}>
                        {sortedDomainLists.map((list) => {
                          const sel = selectedListIds.includes(list.id);
                          const disabled = phase === 'result_shown';
                          return (
                            <Box
                              component="label"
                              key={list.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                border: `1.5px solid ${sel ? tokens.teal : tokens.line}`,
                                borderRadius: '11px',
                                px: 1.25,
                                py: 0.75,
                                cursor: disabled ? 'default' : 'pointer',
                                opacity: disabled ? 0.7 : 1,
                                background: sel
                                  ? dark
                                    ? 'rgba(45,212,191,0.12)'
                                    : '#eafaf6'
                                  : dark
                                    ? 'rgba(255,255,255,0.04)'
                                    : '#fff',
                                transition: '.12s',
                                '&:hover': disabled ? {} : { borderColor: 'rgba(13,148,136,.4)' },
                              }}
                            >
                              <Checkbox
                                checked={sel}
                                onChange={() => handleToggleDomainList(list.id)}
                                size="small"
                                disabled={disabled}
                                sx={{
                                  p: 0,
                                  color: tokens.faint,
                                  '&.Mui-checked': { color: tokens.teal },
                                }}
                              />
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 12.5,
                                  fontWeight: 800,
                                  color: tokens.ink,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                }}
                              >
                                {list.name}
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: 9,
                                    fontWeight: 900,
                                    borderRadius: 999,
                                    px: 0.75,
                                    py: '1px',
                                    color: list.is_system ? '#2563eb' : tokens.teal,
                                    bgcolor: list.is_system
                                      ? dark
                                        ? 'rgba(37,99,235,0.2)'
                                        : '#e8f1ff'
                                      : dark
                                        ? 'rgba(13,148,136,0.2)'
                                        : '#d6f3ee',
                                  }}
                                >
                                  {list.is_system ? '公式' : 'マイ'}
                                </Box>
                              </Box>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 10.5,
                                  fontWeight: 800,
                                  color: tokens.muted,
                                  bgcolor: dark ? 'rgba(255,255,255,0.06)' : '#f2f4f8',
                                  borderRadius: 999,
                                  px: 0.9,
                                  py: '1px',
                                }}
                              >
                                {list.domains.length}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    <Typography
                      sx={{ fontSize: 11.5, fontWeight: 800, color: tokens.muted, mt: 1 }}
                    >
                      選択中:{' '}
                      <Box
                        component="b"
                        sx={{
                          color:
                            selectedDomainsCount > MAX_DOMAINS
                              ? '#dc2626'
                              : selectedDomainsCount > 250
                                ? '#e08600'
                                : tokens.teal,
                        }}
                      >
                        {selectedDomainsCount}
                      </Box>{' '}
                      / {MAX_DOMAINS} ドメイン
                    </Typography>
                  </Box>

                  {/* 検証実行ボタン（Phase 2のみ） */}
                  {phase === 'query_generated' && (
                    <Box sx={{ display: 'flex', gap: 1.25, mt: 1.75 }}>
                      <Button
                        variant="contained"
                        disableElevation
                        startIcon={
                          verifying ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SearchIcon sx={{ fontSize: 18 }} />
                          )
                        }
                        onClick={handleVerify}
                        disabled={verifying || !claim.trim() || selectedListIds.length === 0}
                        sx={{
                          flex: 1,
                          background: ctaGradient,
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: 14,
                          borderRadius: '12px',
                          py: 1.25,
                          textTransform: 'none',
                          boxShadow: '0 8px 20px rgba(40,50,90,.2)',
                          '&:hover': { background: ctaGradient, filter: 'brightness(0.96)' },
                          '&.Mui-disabled': {
                            background: dark ? 'rgba(255,255,255,0.12)' : '#cbd2e0',
                            color: '#fff',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        {verifying ? '検証中...' : 'ファクトチェック実行'}
                      </Button>
                      <Button
                        onClick={handleReset}
                        startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 800,
                          fontSize: 14,
                          color: tokens.muted,
                          bgcolor: dark ? 'rgba(255,255,255,0.05)' : '#fff',
                          border: `1.5px solid ${tokens.line}`,
                          borderRadius: '12px',
                          px: 2,
                          '&:hover': { borderColor: tokens.teal, color: tokens.teal },
                        }}
                      >
                        リセット
                      </Button>
                    </Box>
                  )}

                  {/* エラー表示 */}
                  {error && (
                    <Alert
                      severity="error"
                      sx={{ mt: 1.75, borderRadius: '12px' }}
                      onClose={() => setError(null)}
                    >
                      {error}
                    </Alert>
                  )}
                </Box>
              )}

              {/* 初期フェーズのエラー表示 */}
              {phase === 'initial' && error && (
                <Alert
                  severity="error"
                  sx={{ mt: 1.75, borderRadius: '12px' }}
                  onClose={() => setError(null)}
                >
                  {error}
                </Alert>
              )}
            </Box>

            {/* 結果パネル（Phase 3のみ） */}
            {showResult && result && (
              <Box
                sx={{
                  ...panelSx,
                  animation: 'slideIn 0.4s ease-out',
                  '@keyframes slideIn': {
                    from: { opacity: 0, transform: 'translateX(20px)' },
                    to: { opacity: 1, transform: 'translateX(0)' },
                  },
                }}
              >
                {/* 判定ヘッダー */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.6,
                    pb: 1.75,
                    mb: 1.75,
                    borderBottom: `1px solid ${tokens.line}`,
                  }}
                >
                  <Box
                    component="img"
                    src={verdictSealFromLabel(getJudgementLabel(result.judgement))}
                    alt=""
                    sx={{ width: 48, height: 48, objectFit: 'contain', flexShrink: 0 }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 900,
                        color: getJudgementColor(result.judgement),
                        lineHeight: 1.1,
                      }}
                    >
                      {getJudgementLabel(result.judgement)}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: tokens.muted, mt: 0.25 }}>
                      {result.source_count}件のソースを参照
                    </Typography>
                  </Box>
                  <Tooltip title="案件に共有">
                    <Box
                      component="button"
                      type="button"
                      onClick={handleShareCurrentResult}
                      sx={{
                        ml: 'auto',
                        flexShrink: 0,
                        width: 36,
                        height: 36,
                        p: 0,
                        m: 0,
                        boxSizing: 'border-box',
                        lineHeight: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: dark ? 'rgba(255,255,255,0.05)' : '#fff',
                        border: `1px solid ${tokens.line}`,
                        borderRadius: '10px',
                        color: tokens.muted,
                        cursor: 'pointer',
                        '&:hover': { borderColor: tokens.teal, color: tokens.teal },
                      }}
                    >
                      <ShareIcon sx={{ fontSize: 17, display: 'block' }} />
                    </Box>
                  </Tooltip>
                </Box>

                {/* サマリー */}
                <Box
                  sx={{
                    fontSize: 13.5,
                    lineHeight: 1.65,
                    color: tokens.ink,
                    background: dark ? 'rgba(13,148,136,0.1)' : '#f6f9f8',
                    borderLeft: `3px solid ${tokens.teal}`,
                    borderRadius: '0 10px 10px 0',
                    p: '12px 15px',
                    mb: 1.75,
                  }}
                >
                  {result.summary}
                </Box>

                {/* 詳細理由 */}
                <Accordion
                  defaultExpanded
                  disableGutters
                  elevation={0}
                  sx={{
                    background: accordionBg,
                    border: `1px solid ${tokens.line}`,
                    borderRadius: '12px !important',
                    mb: 1.25,
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: tokens.faint }} />}>
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: tokens.ink }}>
                      詳細な判定理由
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        lineHeight: 1.65,
                        color: tokens.muted,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {result.reason}
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                {/* 参照ソース */}
                {result.sources.length > 0 && (
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                      background: accordionBg,
                      border: `1px solid ${tokens.line}`,
                      borderRadius: '12px !important',
                      mb: 1.25,
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: tokens.faint }} />}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: tokens.ink }}>
                        参照ソース ({result.sources.length}件)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {result.sources.map((source, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            gap: 1.25,
                            alignItems: 'flex-start',
                            py: 1.1,
                            borderTop: index === 0 ? 'none' : `1px solid ${tokens.line}`,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '6px',
                              bgcolor: tokens.teal,
                              color: '#fff',
                              fontSize: 11,
                              fontWeight: 900,
                              display: 'grid',
                              placeItems: 'center',
                              flexShrink: 0,
                              mt: '1px',
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: tokens.ink }}>
                              {source.title}
                            </Typography>
                            <Link
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                fontSize: 11,
                                color: tokens.indigo,
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.25,
                              }}
                            >
                              {source.url}
                              <OpenInNewIcon sx={{ fontSize: 12 }} />
                            </Link>
                          </Box>
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* 思考プロセス */}
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    background: accordionBg,
                    border: `1px solid ${tokens.line}`,
                    borderRadius: '12px !important',
                    mb: 1.25,
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: tokens.faint }} />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PsychologyIcon sx={{ fontSize: 16, color: tokens.teal }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: tokens.ink }}>
                        検証の思考プロセス
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        lineHeight: 1.65,
                        color: tokens.muted,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {result.thought_process}
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                {/* Tavily回答 */}
                {result.tavily_answer && (
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                      background: accordionBg,
                      border: `1px solid ${tokens.line}`,
                      borderRadius: '12px !important',
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: tokens.faint }} />}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: tokens.ink }}>
                        検索エンジンの参考回答
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        sx={{
                          fontSize: 12.5,
                          lineHeight: 1.65,
                          color: tokens.muted,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {result.tavily_answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* 新規検証ボタン */}
                <Button
                  onClick={handleReset}
                  startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
                  fullWidth
                  sx={{
                    mt: 1.75,
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: 14,
                    color: tokens.muted,
                    bgcolor: dark ? 'rgba(255,255,255,0.05)' : '#fff',
                    border: `1.5px solid ${tokens.line}`,
                    borderRadius: '12px',
                    py: 1.1,
                    '&:hover': { borderColor: tokens.teal, color: tokens.teal },
                  }}
                >
                  新しい検証を始める
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ドメインリスト管理ダイアログ */}
      <DomainListManager
        open={showDomainManager}
        onClose={() => setShowDomainManager(false)}
        onUpdate={loadDomainLists}
      />

      {/* 案件共有ダイアログ */}
      <WorkspaceShareDialog
        open={showShareDialog}
        onClose={() => {
          setShowShareDialog(false);
          setShareTarget(null);
        }}
        onShare={handleShareToWorkspace}
      />

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DomainFactCheck;
